import { IUnityScreenshotService, UnityScreenshotConfig, ScreenshotStorageProvider, SceneUpdateProvider } from './types';
import { filter, first } from 'rxjs';
import type { RendererBridge } from '@/modules/rendererBridge';

const MAX_UPLOAD_EDGE = 1280;
const UPLOAD_FORMAT = 'jpeg';
const UPLOAD_QUALITY = 0.82;

/** The writes a capture makes that count as edits (editCount): the scene's PATCH to its new cover. Storing and deleting files don't. */
const CAPTURE_EDITS = 1;

export class UnityScreenshotService implements IUnityScreenshotService {
  private config: UnityScreenshotConfig;
  private abortControllers: Set<AbortController> = new Set();
  private iframeRef: HTMLIFrameElement | null = null;
  private requestIdCounter: number = 0;
  // Each scene's last cover taken, and the edit count it shows the scene at (undefined: edited while it was taken)
  private lastCapture = new Map<string, { url: string; edits: number | undefined }>();
  private capturing = new Set<string>();

  constructor(
    private readonly storageProvider: ScreenshotStorageProvider,
    private readonly sceneUpdateProvider: SceneUpdateProvider,
    private readonly bridge: RendererBridge,
    config?: Partial<UnityScreenshotConfig>
  ) {
    this.config = {
      format: 'png',
      quality: 0.9,
      ...config
    };
  }

  /**
   * Set the renderer's iframe; captures fail fast while there is none
   */
  setIframeRef(iframe: HTMLIFrameElement | null): void {
    this.iframeRef = iframe;
  }

  /**
   * Check if Unity canvas is ready for screenshot capture (via iframe)
   */
  private async isUnityCanvasReady(): Promise<boolean> {
    if (!this.iframeRef || !this.iframeRef.contentWindow) {
      console.log('Iframe not available for canvas check');
      return false;
    }

    return new Promise((resolve) => {
      const requestId = `ready-check-${this.requestIdCounter++}`;
      const timeout = setTimeout(() => {
        answer.unsubscribe();
        console.log('Canvas ready check timed out');
        resolve(false);
      }, 1000);

      const answer = this.bridge.on('canvasReadyResponse')
        .pipe(filter((message) => message.requestId === requestId), first())
        .subscribe((message) => {
          clearTimeout(timeout);
          resolve(message.ready);
        });

      this.bridge.send({ type: 'checkCanvasReady', requestId });
    });
  }

  async captureScreenshot(): Promise<string> {
    if (!this.iframeRef || !this.iframeRef.contentWindow) {
      throw new Error('Unity iframe not available - make sure Unity is loaded');
    }

    return new Promise((resolve, reject) => {
      const requestId = `screenshot-${this.requestIdCounter++}`;
      const timeout = setTimeout(() => {
        answer.unsubscribe();
        reject(new Error('Screenshot request timed out after 5 seconds'));
      }, 5000);

      const answer = this.bridge.on('screenshotResponse')
        .pipe(filter((message) => message.requestId === requestId), first())
        .subscribe((message) => {
          clearTimeout(timeout);
          if (message.success && message.dataUrl) {
            resolve(message.dataUrl);
          } else {
            reject(new Error(message.error || 'Screenshot capture failed'));
          }
        });

      // Request screenshot from the renderer
      this.bridge.send({
        type: 'requestScreenshot',
        requestId,
        format: this.config.format,
        quality: this.config.quality
      });
    });
  }

  cancelAllOperations(): void {
    this.abortControllers.forEach(controller => controller.abort());
    this.abortControllers.clear();
  }

  /**
   * Takes the scene's cover: a new file each time where storage versions the name (the CDN), so once the scene points at
   * it the one before is deleted; where storage keeps the name (Supabase), it replaces the one before, which stays. Nothing
   * is taken while the last cover that reached the server still shows the scene as it is, or while one is being taken.
   */
  async captureScreenshotAutomatic(sceneInstanceId: string, onSuccess?: (url: string) => void, onError?: (error: Error) => void, _signal?: AbortSignal): Promise<string> {
    const editsBefore = this.sceneUpdateProvider.editCount();
    const last = this.lastCapture.get(sceneInstanceId);
    if (last && last.edits === editsBefore) return last.url;
    if (this.capturing.has(sceneInstanceId)) return last?.url ?? '';
    this.capturing.add(sceneInstanceId);

    const abortController = new AbortController();
    this.abortControllers.add(abortController);

    try {
      if (!this.iframeRef || !this.iframeRef.contentWindow) {
        throw new Error('Unity iframe not available - make sure Unity is loaded');
      }

      // Wait for Unity canvas to be ready
      let readyAttempts = 0;
      const maxReadyAttempts = 100; // Wait up to 10 seconds for Unity to be ready
      
      console.log('Waiting for Unity canvas to be ready...');
      while (!(await this.isUnityCanvasReady()) && readyAttempts < maxReadyAttempts) {
        // Check for cancellation
        if (abortController.signal.aborted) {
          throw new Error('Screenshot operation was cancelled');
        }
        
        await new Promise(resolve => setTimeout(resolve, 100));
        readyAttempts++;
        if (readyAttempts % 10 === 0) {
          console.log(`Unity canvas readiness check: ${readyAttempts}/${maxReadyAttempts}`);
        }
      }
      
      if (!(await this.isUnityCanvasReady())) {
        console.error('Unity canvas not ready after waiting - Unity may not be fully loaded');
        throw new Error('Unity canvas not ready after waiting - Unity may not be fully loaded');
      }
          
      // Use requestAnimationFrame to capture during Unity's render cycle
      const screenshotDataURL = await new Promise<string>((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 20;
        let lastError: string = '';
        
        const captureFrame = async () => {
          // Check for cancellation before each attempt
          if (abortController.signal.aborted) {
            reject(new Error('Screenshot operation was cancelled'));
            return;
          }
          
          attempts++;
          try {
            const dataURL = await this.captureScreenshot();
            resolve(dataURL);
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            lastError = errorMessage;
            console.log(`Screenshot attempt ${attempts}/${maxAttempts} failed:`, errorMessage);
            
            if (attempts < maxAttempts) {
              // Add a small delay between attempts to give Unity more time
              setTimeout(() => {
                if (!abortController.signal.aborted) {
                  requestAnimationFrame(captureFrame);
                }
              }, 100);
            } else {
              reject(new Error(`Failed to capture after ${maxAttempts} attempts: ${lastError}`));
            }
          }
        };
        
        // Start capturing on next animation frame
        requestAnimationFrame(captureFrame);
      });
      
      const blob = await this.encodeForUpload(screenshotDataURL);
      // The cover the scene points at before this one, whose file goes once the scene points at the new one
      const previous = await this.sceneUpdateProvider.sceneCoverImage(sceneInstanceId).catch(() => null);
      const { url: publicUrl, error: uploadError } = await this.storageProvider.uploadScreenshot({
        sceneInstanceId,
        blob,
        format: UPLOAD_FORMAT
      });

      if (uploadError) {
        throw uploadError;
      }

      // Update scene with the new cover image URL, then read the count: any edit but that one came while this was taken
      const landed = await this.sceneUpdateProvider.updateSceneCoverImage(sceneInstanceId, publicUrl);
      const editsAfter = this.sceneUpdateProvider.editCount();
      // The capture is the scene's cover only once that change has reached the server: one refused, or not there in
      // time, is taken again next time, edited or not
      if (landed) this.lastCapture.set(sceneInstanceId, { url: publicUrl, edits: editsAfter - editsBefore === CAPTURE_EDITS ? editsAfter : undefined });
      else this.lastCapture.delete(sceneInstanceId);

      // Only once the scene points at the new cover on the server is the old one's file deleted, and never when it is the
      // file just stored (a store that keeps the name asked replaces it in place); the storage provider tells the two apart
      if (landed && previous) {
        await this.storageProvider.deleteScreenshot(sceneInstanceId, previous, publicUrl).catch(error => console.warn('[screenshots] the previous cover could not be deleted', error));
      }

      if (onSuccess) {
        onSuccess(publicUrl);
      }

      return publicUrl;

    } catch (error) {
      if (onError) {
        onError(error instanceof Error ? error : new Error('Automatic screenshot capture failed'));
      }

      throw error;
    } finally {
      // Clean up the abort controller
      this.abortControllers.delete(abortController);
      this.capturing.delete(sceneInstanceId);
    }
  }

  private async encodeForUpload(dataURL: string): Promise<Blob> {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not decode the captured frame'));
      img.src = dataURL;
    });

    const scale = Math.min(1, MAX_UPLOAD_EDGE / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return this.dataURLToBlob(dataURL);
    }
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, `image/${UPLOAD_FORMAT}`, UPLOAD_QUALITY)
    );
    return blob ?? this.dataURLToBlob(dataURL);
  }

  private dataURLToBlob(dataURL: string): Blob {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }
} 